/**
 * Inventory storage policy
 *
 * Both paths that produce a source code inventory — the AI conversion of a
 * questionnaire and the upload inside "Fill from source document" — used to
 * store what they had unconditionally, so the last write won. A run whose
 * second document chunk failed could therefore overwrite a complete
 * extraction, and the next fill would match against half a document.
 *
 * These helpers are the shared decision: how complete an inventory is, whether
 * it came out of a truncated extraction, and whether it may replace what the
 * project already holds.
 */

import {
  SourceCodeEntry,
  SourceCodeInventory,
  SourceCodeOption,
} from "types/sourceCodes.types"

/** Why {@link shouldReplaceStored} decided the way it did */
export type StoreDecisionReason =
  /** The project holds no inventory yet */
  | "no-existing"
  /** The incoming extraction is complete and the stored one was truncated */
  | "incoming-complete"
  /** The incoming extraction is truncated and the stored one is not */
  | "incoming-partial-existing-complete"
  /** Equally complete runs, but the incoming one found less */
  | "incoming-smaller"
  /** Equally complete runs, and the incoming one found at least as much */
  | "incoming-larger-or-equal"

/** Outcome of comparing an incoming inventory with the stored one */
export interface StoreDecision {
  replace: boolean
  reason: StoreDecisionReason
}

/** What an inventory holds, for the dialog summary */
export interface InventoryCounts {
  entries: number
  lists: number
  options: number
  items: number
}

/**
 * True when the extraction that produced this inventory lost at least one
 * document chunk, so questions are missing rather than merely uncoded.
 *
 * An inventory stored before the service reported extraction stats carries
 * none; it is treated as complete, which is what it was assumed to be.
 *
 * @param inventory - Inventory to inspect (null and undefined are not partial)
 */
export const isPartial = (
  inventory: SourceCodeInventory | null | undefined
): boolean => (inventory?.source?.extraction?.failedChunks ?? 0) > 0

/**
 * Options of every shared list, by list id.
 */
const listOptionsById = (
  inventory: SourceCodeInventory
): Map<string, SourceCodeOption[]> => {
  const byId = new Map<string, SourceCodeOption[]>()
  for (const list of inventory.lists ?? []) {
    byId.set(list.id, list.options ?? [])
  }
  return byId
}

/**
 * Resolves one question's options against the inventory's shared lists.
 */
const resolveOptions = (
  entry: SourceCodeEntry,
  byId: Map<string, SourceCodeOption[]>
): SourceCodeOption[] => {
  const own = entry.options ?? []
  if (own.length > 0 || !entry.listRef) return own
  return byId.get(entry.listRef) ?? own
}

/**
 * The options a question actually offers: its own when it has them, otherwise
 * the shared list it points at. Questions that say "show the list from the
 * previous section" carry no options of their own.
 *
 * @param entry - Question to resolve options for
 * @param inventory - Inventory the question belongs to (holds the lists)
 */
export const effectiveOptions = (
  entry: SourceCodeEntry,
  inventory: SourceCodeInventory
): SourceCodeOption[] => resolveOptions(entry, listOptionsById(inventory))

/**
 * How much of a document an inventory captured: questions plus the options and
 * grid items hanging off them. Deliberately crude — it only has to separate a
 * truncated run from a full one, not judge quality.
 *
 * Options reached through a shared list count once per question that uses it,
 * which is what a fill would match against.
 *
 * @param inventory - Inventory to measure
 * @returns Entries + options + items
 */
export const inventoryScore = (
  inventory: SourceCodeInventory | null | undefined
): number => {
  if (!inventory) return 0
  const entries = inventory.entries ?? []
  const byId = listOptionsById(inventory)
  let score = entries.length
  for (const entry of entries) {
    score += resolveOptions(entry, byId).length + (entry.items ?? []).length
  }
  return score
}

/**
 * Decides whether an inventory that has just been extracted may replace the one
 * stored with the project.
 *
 * The rules, in order:
 * 1. Nothing stored yet — store it.
 * 2. A truncated extraction never replaces a complete one.
 * 3. A complete extraction always replaces a truncated one.
 * 4. Between two equally complete (or equally truncated) runs, the one that
 *    found at least as much wins; extraction is not stable across runs of the
 *    same document, so an equal score keeps the fresher copy.
 *
 * @param existing - Inventory currently stored with the project, or null
 * @param incoming - Inventory just produced
 * @returns Whether to store the incoming one, and why
 */
export const shouldReplaceStored = (
  existing: SourceCodeInventory | null,
  incoming: SourceCodeInventory
): StoreDecision => {
  if (!existing) return { replace: true, reason: "no-existing" }

  const incomingPartial = isPartial(incoming)
  const existingPartial = isPartial(existing)

  if (incomingPartial && !existingPartial) {
    return { replace: false, reason: "incoming-partial-existing-complete" }
  }
  if (!incomingPartial && existingPartial) {
    return { replace: true, reason: "incoming-complete" }
  }

  return inventoryScore(incoming) >= inventoryScore(existing)
    ? { replace: true, reason: "incoming-larger-or-equal" }
    : { replace: false, reason: "incoming-smaller" }
}

/**
 * Counts for the stored-codes summary. The extraction stats are preferred when
 * the service sent them (they describe the run that produced the inventory);
 * anything they leave out is counted off the inventory itself.
 *
 * @param inventory - Inventory to summarise
 */
export const inventoryCounts = (
  inventory: SourceCodeInventory
): InventoryCounts => {
  const entries = inventory.entries ?? []
  const byId = listOptionsById(inventory)
  let options = 0
  let items = 0
  for (const entry of entries) {
    options += resolveOptions(entry, byId).length
    items += (entry.items ?? []).length
  }

  const extraction = inventory.source?.extraction
  return {
    entries: extraction?.entries ?? entries.length,
    lists: extraction?.lists ?? inventory.lists?.length ?? 0,
    options: extraction?.options ?? options,
    items: extraction?.items ?? items,
  }
}

/**
 * Turns the chunk indexes the service reports into part numbers the user sees.
 *
 * The field is named for indexes, so it is read as zero-based; an entry that
 * would then point past the last chunk says the service is already numbering
 * from one, and is taken at face value.
 */
/**
 * The converter reports failed chunk indexes ONE-based (part 1 is the first
 * document chunk; see SourceCodeExtractionService: `failedChunkIndexes.push(c + 1)`).
 * Keep them as they are, dropping anything outside 1..chunks.
 */
const toPartNumbers = (indexes: number[], chunks: number): number[] => {
  const parts = indexes.filter(
    (index) => Number.isInteger(index) && index >= 1 && (chunks <= 0 || index <= chunks)
  )
  return [...new Set(parts)].sort((a, b) => a - b)
}

/**
 * Names the document parts an extraction lost, for a message the user reads:
 * "part 2 of 2", "parts 2 and 3 of 4", or — when the service did not say which
 * parts failed — "1 part of 2".
 *
 * @param inventory - Inventory to describe
 * @returns The phrase, or null when the extraction lost nothing
 */
export const failedPartsPhrase = (
  inventory: SourceCodeInventory | null | undefined
): string | null => {
  const extraction = inventory?.source?.extraction
  if (!extraction || extraction.failedChunks <= 0) return null

  const chunks = extraction.chunks
  const parts = toPartNumbers(extraction.failedChunkIndexes ?? [], chunks)
  if (parts.length === 0) {
    return `${extraction.failedChunks} ${
      extraction.failedChunks === 1 ? "part" : "parts"
    } of ${chunks}`
  }
  const list =
    parts.length === 1
      ? `${parts[0]}`
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
  return `${parts.length === 1 ? "part" : "parts"} ${list} of ${chunks}`
}

/**
 * The failed-parts phrase as a sentence opener: "Part 2 of 2 of the document
 * failed to extract".
 *
 * @param inventory - Inventory to describe
 * @returns The sentence, or null when the extraction lost nothing
 */
export const failedPartsSentence = (
  inventory: SourceCodeInventory | null | undefined
): string | null => {
  const phrase = failedPartsPhrase(inventory)
  if (!phrase) return null
  return `${phrase.charAt(0).toUpperCase()}${phrase.slice(
    1
  )} of the document failed to extract`
}
